import type {
  EngineDynamicWallet,
  EngineGameResponse,
  EnginePlayResponse,
  EngineWheelConfig,
  LoadedEngineGame,
} from './engine.models';

export class EngineApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'EngineApiError';
  }
}

export class EngineAuthenticationError extends EngineApiError {
  constructor(status: number) {
    super(
      'Your board-engine session is unavailable. Sign in to continue.',
      status,
    );
    this.name = 'EngineAuthenticationError';
  }
}

export class EngineClient {
  constructor(
    private readonly baseUrl: string,
    private readonly timezone: string,
    private readonly request: typeof fetch = (input, init) =>
      fetch(input, init),
  ) {}

  loginUrl(returnUrl: string): string {
    const url = new URL(`${this.baseUrl.replace(/\/$/, '')}/login`);
    url.searchParams.set('redirect', returnUrl);
    return url.href;
  }

  async authenticate(gameId: string, token: string): Promise<void> {
    if (!token.trim()) throw new Error('A launch token is required.');
    await this.gameResponse(gameId, token.trim());
  }

  private async gameResponse(
    gameId: string,
    token?: string,
  ): Promise<EngineGameResponse> {
    if (!gameId.trim())
      throw new Error('A gameId is required to load the wheel.');
    const url = this.endpoint('game');
    url.searchParams.set('gameId', gameId);
    const response = await this.json<EngineGameResponse>(
      url.href,
      true,
      'GET',
      token,
    );
    if (!response?.gameConfigUrl || !response.startPosition) {
      throw new Error('The engine returned an invalid game response.');
    }
    return response;
  }

  async load(gameId: string, token?: string): Promise<LoadedEngineGame> {
    const response = await this.gameResponse(gameId, token);
    const [config, labels] = await Promise.all([
      this.asset<EngineWheelConfig>(
        response.gameConfigUrl,
        response.fallbackGameConfigUrl,
        (value) =>
          value?.uuid === gameId &&
          Array.isArray(value.segments) &&
          value.segments.length > 0,
      ),
      this.asset<Record<string, string>>(
        response.gameLabelsUrl,
        response.fallbackLabelsUrl,
        (value) =>
          !!value && typeof value === 'object' && !Array.isArray(value),
      ).catch(() => ({})),
    ]);
    return { response, config, labels };
  }

  async play(): Promise<EnginePlayResponse> {
    const response = await this.json<EnginePlayResponse>(
      this.endpoint('game/play').href,
      true,
      'POST',
    );
    if (
      !response ||
      !Array.isArray(response.actions) ||
      !Number.isFinite(response.nextRoll) ||
      !Number.isInteger(response.rollsAvailable) ||
      response.rollsAvailable < 0
    ) {
      throw new Error('The engine returned an invalid spin response.');
    }
    return response;
  }

  async dynamicWallets(): Promise<EngineDynamicWallet[]> {
    const response = await this.json<EngineDynamicWallet[]>(
      this.endpoint('game/dynamic-wallets').href,
      true,
    );
    if (
      !Array.isArray(response) ||
      response.some(
        (wallet) =>
          !wallet ||
          typeof wallet.walletId !== 'string' ||
          !wallet.walletId.trim() ||
          typeof wallet.currency !== 'string' ||
          !wallet.currency.trim() ||
          !Number.isFinite(wallet.coins) ||
          wallet.coins < 0 ||
          [wallet.openedAt, wallet.creditUntil, wallet.debitUntil].some(
            (date) =>
              typeof date !== 'string' || !Number.isFinite(Date.parse(date)),
          ),
      ) ||
      new Set(response.map((wallet) => wallet.walletId)).size !==
        response.length
    ) {
      throw new Error(
        'The engine returned an invalid rewards history response.',
      );
    }
    return response;
  }

  private endpoint(path: string): URL {
    const url = new URL(`${this.baseUrl.replace(/\/$/, '')}/api/${path}`);
    url.searchParams.set('timezone', this.timezone);
    return url;
  }

  private async asset<T>(
    primary?: string,
    fallback?: string,
    valid?: (value: T) => boolean,
  ): Promise<T> {
    let error: unknown = new Error('The game configuration URL is missing.');
    for (const target of new Set(
      [primary, fallback].filter((url): url is string => !!url),
    )) {
      try {
        const url = new URL(target);
        if (!['https:', 'http:'].includes(url.protocol))
          throw new Error('Invalid game asset URL.');
        const value = await this.json<T>(url.href, false);
        if (valid && !valid(value))
          throw new Error('Invalid game configuration.');
        return value;
      } catch (cause) {
        error = cause;
      }
    }
    throw error;
  }

  private async json<T>(
    url: string,
    authenticated: boolean,
    method = 'GET',
    token?: string,
  ): Promise<T> {
    const response = await this.request(url, {
      method,
      credentials: authenticated ? 'include' : 'omit',
      redirect: authenticated ? 'manual' : 'follow',
      headers: {
        Accept: 'application/json',
        ...(authenticated && token
          ? { 'X-Custom-Authorization': `Bearer ${token}` }
          : {}),
      },
    });
    if (
      authenticated &&
      (response.type === 'opaqueredirect' ||
        [301, 302, 303, 307, 308, 401, 403].includes(response.status))
    ) {
      throw new EngineAuthenticationError(response.status);
    }
    if (!response.ok) {
      throw new EngineApiError(
        `The engine request failed (${response.status}). Please try again.`,
        response.status,
      );
    }
    try {
      return (await response.json()) as T;
    } catch {
      throw new EngineApiError(
        'The engine returned an invalid JSON response.',
        response.status,
      );
    }
  }
}
